"use client";

// Befehlspalette (⌘K / Strg+K): Bereiche, Schnellaktionen und die Suche in
// Mitgliedern — von jeder Seite aus. Was ClubDesk in fuenf Klicks
// versteckt, steht hier in einem Tastendruck.
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import * as Dialog from "@radix-ui/react-dialog";
import { CalendarPlus, CornerDownLeft, FilePlus2, MailPlus, Search, UserPlus, type LucideIcon } from "lucide-react";
import { ALLE_EINTRAEGE } from "@/lib/navigation";
import { cn } from "@/lib/utils";

type Befehl = { id: string; label: string; hinweis: string; icon: LucideIcon; href: string; gruppe: string };

const AKTIONEN: Befehl[] = [
  { id: "neu-mitglied", label: "Mitglied anlegen", hinweis: "Neue Person erfassen", icon: UserPlus, href: "/mitglieder/neu", gruppe: "Schnell" },
  { id: "neu-termin", label: "Termin anlegen", hinweis: "Training, Spiel, Sitzung", icon: CalendarPlus, href: "/kalender/neu", gruppe: "Schnell" },
  { id: "neu-rechnung", label: "Rechnung schreiben", hinweis: "Freie Rechnung an ein Mitglied", icon: FilePlus2, href: "/rechnungen/neu", gruppe: "Schnell" },
  { id: "neu-mail", label: "Serienmail schreiben", hinweis: "An Gruppen oder alle", icon: MailPlus, href: "/nachrichten/neu", gruppe: "Schnell" },
];

const BEREICHE: Befehl[] = ALLE_EINTRAEGE.map((e) => ({
  id: e.href,
  label: e.label,
  hinweis: e.hinweis,
  icon: e.icon,
  href: e.href,
  gruppe: "Bereiche",
}));

export function useBefehlspalette() {
  const [offen, setOffen] = useState(false);
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOffen((o) => !o);
      }
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, []);
  return { offen, setOffen };
}

export function CommandPalette({ offen, setOffen }: { offen: boolean; setOffen: (o: boolean) => void }) {
  const router = useRouter();
  const [q, setQ] = useState("");
  const [index, setIndex] = useState(0);
  const listeRef = useRef<HTMLDivElement>(null);

  const treffer = useMemo(() => {
    const s = q.trim().toLowerCase();
    const alle = [...AKTIONEN, ...BEREICHE];
    const gefiltert = s
      ? alle.filter((b) => b.label.toLowerCase().includes(s) || b.hinweis.toLowerCase().includes(s))
      : alle;
    if (s) {
      gefiltert.push({
        id: "suche-mitglieder",
        label: `„${q.trim()}“ in Mitgliedern suchen`,
        hinweis: "Name, Mitgliedsnummer, Mail, Ort",
        icon: Search,
        href: `/mitglieder?q=${encodeURIComponent(q.trim())}`,
        gruppe: "Suche",
      });
    }
    return gefiltert;
  }, [q]);

  useEffect(() => setIndex(0), [q]);
  useEffect(() => {
    if (!offen) setQ("");
  }, [offen]);

  function oeffne(b: Befehl) {
    setOffen(false);
    router.push(b.href);
  }

  function taste(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndex((i) => Math.min(i + 1, treffer.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && treffer[index]) {
      e.preventDefault();
      oeffne(treffer[index]);
    }
  }

  useEffect(() => {
    listeRef.current?.querySelector(`[data-index="${index}"]`)?.scrollIntoView({ block: "nearest" });
  }, [index]);

  let zuletzt = "";
  return (
    <Dialog.Root open={offen} onOpenChange={setOffen}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/30 backdrop-blur-[2px]" />
        <Dialog.Content
          aria-describedby={undefined}
          className="bg-bg-elevated shadow-modal animate-slide-up fixed top-[12vh] left-1/2 z-50 w-[min(640px,calc(100vw-24px))] -translate-x-1/2 overflow-hidden rounded-2xl ring-1 ring-black/[0.06] dark:ring-white/[0.08]"
        >
          <Dialog.Title className="sr-only">Befehlspalette</Dialog.Title>
          <div className="border-border-subtle flex items-center gap-3 border-b px-4">
            <Search className="text-text-tertiary h-4 w-4 shrink-0" aria-hidden />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={taste}
              placeholder="Bereich, Aktion oder Mitglied suchen …"
              className="text-body-md text-text-primary placeholder:text-text-tertiary h-14 flex-1 bg-transparent outline-none"
            />
            <kbd className="text-caption text-text-tertiary border-border rounded border px-1.5 py-0.5">esc</kbd>
          </div>
          <div ref={listeRef} className="max-h-[min(420px,60vh)] overflow-y-auto p-2">
            {treffer.length === 0 && (
              <p className="text-body-sm text-text-tertiary px-3 py-8 text-center">Nichts gefunden.</p>
            )}
            {treffer.map((b, i) => {
              const kopf = b.gruppe !== zuletzt ? b.gruppe : null;
              zuletzt = b.gruppe;
              const Icon = b.icon;
              return (
                <div key={b.id}>
                  {kopf && <p className="text-overline text-text-tertiary px-3 pt-2 pb-1">{kopf}</p>}
                  <button
                    type="button"
                    data-index={i}
                    onMouseEnter={() => setIndex(i)}
                    onClick={() => oeffne(b)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left",
                      i === index ? "bg-bg-secondary" : "",
                    )}
                  >
                    <Icon className={cn("h-4 w-4 shrink-0", i === index ? "text-accent" : "text-text-tertiary")} aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="text-body-sm text-text-primary block truncate">{b.label}</span>
                      <span className="text-caption text-text-tertiary block truncate">{b.hinweis}</span>
                    </span>
                    {i === index && <CornerDownLeft className="text-text-tertiary h-3.5 w-3.5" aria-hidden />}
                  </button>
                </div>
              );
            })}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
